"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";

// SSR-safe providers tree.
//
// The AuthProvider is mounted client-only because it subscribes to Supabase
// auth state — running it during SSG triggers a destructure error in the
// @supabase/ssr / @tanstack/react-query interaction during prerender.
// (Specifically: somewhere in that chain, `const { auth } = options` is
// called with options undefined when the browser-only code path runs on
// the server.)
//
// Deferring AuthProvider until after mount lets every page prerender as
// pure HTML. The user's auth state hydrates a few ms after first paint.
// Same UX as before; no SSR failures.
export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient());
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const tree = (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {mounted ? (
          <AuthProvider>
            {children}
            <Toaster />
            <Sonner />
          </AuthProvider>
        ) : (
          // Server / pre-hydration: render children without auth context.
          // useAuth() throws if called outside AuthProvider, so all components
          // that call it must be inside a NoSSR wrapper or guard with mounted.
          children
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
  return tree;
}
